import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import LocationMarker from 'components/Icons/LocationMarker'
import Pencil from 'components/Icons/Pencil'
import Pushpin from 'components/Icons/Pushpin'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { withMarkers } from '../withMarkers'
import HeroCompactPhone from './HeroCompactPhone'

const t = buildTranslate('live_updates.overview.hero')
const tPublished = buildTranslate('live_updates.overview.hero.published')

const BOLD_MARKER = '[[bold]]'

const boldRun = (copy: string) => (
  <Text.BodyText3 tag="span" bold color="grey90">
    {copy}
  </Text.BodyText3>
)

const withBold = (copy: string, boldCopy: string): React.ReactNode =>
  withMarkers(copy, [[BOLD_MARKER, boldRun(boldCopy)]])

const BENEFIT_GAP = '14px'
const HERO_GAP = '0px'
const COMPACT_STACK_BREAKPOINT = '860px'
// The prototype's eyebrow and bullet icons are spearmint ink, not the page's
// belize blue.
const BENEFIT_ICON_COLOR = palette.spearmint60
const PILL_GAP = '7px'
const PILL_MARGIN = '2px 0 18px'
const PILL_PADDING = '5px 12px 5px 10px'
const PILL_RADIUS = '20px'
const PILL_DOT_SIZE = '8px'

const PublishedPill = () => {
  const [css] = useStyletron()

  return (
    <Text.BodyText4
      tag="span"
      bold
      color={palette.nephritis70}
      className={css({
        display: 'inline-flex',
        alignSelf: 'flex-start',
        alignItems: 'center',
        gap: PILL_GAP,
        margin: PILL_MARGIN,
        padding: PILL_PADDING,
        borderRadius: PILL_RADIUS,
        backgroundColor: palette.nephritis10,
      })}
    >
      <span
        aria-hidden="true"
        className={css({
          width: PILL_DOT_SIZE,
          height: PILL_DOT_SIZE,
          borderRadius: '50%',
          backgroundColor: palette.nephritis70,
        })}
      />
      {tPublished('pill')}
    </Text.BodyText4>
  )
}

type Benefit = {
  key: string
  icon: React.ReactNode
  copy: React.ReactNode
}

export interface HeroSectionProps {
  hasPublished: boolean
  /** The merchant's uploaded logo + name — the decorative Live Activity mock
   * shows the real merchant identity (falling back to a generic "Your
   * brand" placeholder before it loads), the same as the live per-
   * configuration preview on the Configuration tab. */
  appIconUrl: string | undefined
  merchantName: string | undefined
}

const HeroSection = ({
  hasPublished,
  appIconUrl,
  merchantName,
}: HeroSectionProps) => {
  const [css] = useStyletron()
  const tBenefit = hasPublished ? tPublished : t

  const benefits: Benefit[] = [
    {
      key: 'visibility',
      icon: <LocationMarker fillColor={BENEFIT_ICON_COLOR} />,
      copy: withBold(
        tBenefit('benefit_visibility'),
        tBenefit('benefit_visibility_bold')
      ),
    },
    {
      key: 'push',
      icon: <Pushpin fillColor={BENEFIT_ICON_COLOR} />,
      copy: withBold(tBenefit('benefit_push'), tBenefit('benefit_push_bold')),
    },
    {
      key: 'control',
      icon: <Pencil fillColor={BENEFIT_ICON_COLOR} />,
      copy: withBold(
        tBenefit('benefit_control'),
        tBenefit('benefit_control_bold')
      ),
    },
  ]

  return (
    <div
      data-testid="lu-overview-hero"
      className={css({
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: HERO_GAP,
        alignItems: 'stretch',
        borderTopLeftRadius: '12px',
        borderTopRightRadius: '12px',
        borderBottomRightRadius: '12px',
        borderBottomLeftRadius: '12px',
        [`@media (max-width: ${COMPACT_STACK_BREAKPOINT})`]: {
          gridTemplateColumns: '1fr',
        },
        backgroundColor: palette.white,
        border: `1px solid ${palette.grey20}`,
        overflow: 'hidden',
      })}
    >
      <div
        className={css({
          minWidth: 0,
          padding: '40px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        })}
      >
        <Text.BodyText5
          bold
          className={css({
            display: 'block',
            color: BENEFIT_ICON_COLOR,
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            marginBottom: '10px',
          })}
        >
          {t('eyebrow')}
        </Text.BodyText5>
        {hasPublished && <PublishedPill />}
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: BENEFIT_GAP,
          })}
        >
          {benefits.map(benefit => (
            <div
              key={benefit.key}
              className={css({
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
              })}
            >
              <span
                aria-hidden="true"
                className={css({
                  display: 'flex',
                  flexShrink: 0,
                  marginTop: '2px',
                })}
              >
                {benefit.icon}
              </span>
              <Text.BodyText3
                tag="p"
                color="grey70"
                className={css({ margin: 0 })}
              >
                {benefit.copy}
              </Text.BodyText3>
            </div>
          ))}
        </div>
      </div>

      <div
        aria-hidden="true"
        className={css({
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingTop: '32px',
          paddingRight: '24px',
          paddingBottom: '32px',
          paddingLeft: '24px',
          backgroundImage: `linear-gradient(158deg, ${palette.mockWashHeroStart}, ${palette.mockWashEnd})`,
        })}
      >
        <HeroCompactPhone appIconUrl={appIconUrl} merchantName={merchantName} />
      </div>
    </div>
  )
}

export default HeroSection
